import { type DocumentSummary, formatBytes, getPreset, PRESET_CATEGORY_LABELS } from "@cva/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Clapperboard,
  ExternalLink,
  FileImage,
  LayoutTemplate,
  MoreHorizontal,
  PencilRuler,
  Printer,
} from "lucide-react";
import type { ReactNode } from "react";
import { formatRelative } from "../lib/utils";
import { CommentCount } from "./collab";
import { Button } from "./ui/button";
import { ContextMenu } from "./ui/context-menu";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "./ui/dropdown";

export function PresetIcon({
  presetId,
  className = "size-5",
}: {
  presetId: string;
  className?: string;
}) {
  const category = getPreset(presetId)?.category;
  if (category === "reel") return <Clapperboard className={className} />;
  if (category === "print") return <Printer className={className} />;
  return <FileImage className={className} />;
}

export function DocumentCard({
  orgId,
  document,
  subtitle,
  menuItems,
}: {
  orgId: string;
  document: DocumentSummary;
  subtitle?: ReactNode;
  /** Actions of the „…“ menu; a right click shows them too, below „Öffnen“. */
  menuItems?: ReactNode;
}) {
  const navigate = useNavigate();
  const preset = getPreset(document.formatPreset);
  const ratio = document.width / document.height;
  const href = `/o/${orgId}/documents/${document.id}`;
  return (
    <ContextMenu
      menu={
        <>
          <DropdownItem
            onSelect={() =>
              void navigate({
                to: "/o/$orgId/documents/$documentId",
                params: { orgId, documentId: document.id },
              })
            }
          >
            <PencilRuler /> Öffnen
          </DropdownItem>
          <DropdownItem onSelect={() => window.open(href, "_blank", "noopener")}>
            <ExternalLink /> In neuem Tab öffnen
          </DropdownItem>
          {menuItems ? (
            <>
              <DropdownSeparator />
              {menuItems}
            </>
          ) : null}
        </>
      }
    >
      <div className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xs transition hover:border-brand-300 hover:shadow-md">
        <Link
          to="/o/$orgId/documents/$documentId"
          params={{ orgId, documentId: document.id }}
          className="block"
        >
          <div className="relative flex h-40 items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-50">
            {document.thumbnailUrl ? (
              <img
                src={document.thumbnailUrl}
                alt=""
                loading="lazy"
                className="max-h-32 max-w-[85%] rounded-sm shadow-sm ring-1 ring-black/5"
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-400 shadow-xs"
                style={
                  ratio >= 1
                    ? { width: 120, height: 120 / ratio }
                    : { height: 120, width: 120 * ratio }
                }
              >
                <PresetIcon presetId={document.formatPreset} />
              </div>
            )}
            {document.isTemplate ? (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-zinc-900/80 px-2 py-0.5 text-[11px] font-medium text-white">
                <LayoutTemplate className="size-3" /> Vorlage
              </span>
            ) : null}
            {document.openComments > 0 ? (
              <CommentCount count={document.openComments} className="absolute right-2 bottom-2" />
            ) : null}
            {document.activeUsers.length > 0 ? (
              <span
                className="absolute bottom-2 left-2 rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white shadow"
                title={`Gerade geöffnet von ${document.activeUsers.map((user) => user.name).join(", ")}`}
              >
                ● {document.activeUsers.map((user) => user.name.split(" ")[0]).join(", ")}
              </span>
            ) : null}
          </div>
          <div className="border-t border-zinc-100 px-3.5 py-3">
            <p className="truncate text-sm font-medium text-zinc-900">{document.title}</p>
            <p className="mt-0.5 truncate text-xs text-zinc-500">
              {subtitle ??
                (preset
                  ? `${preset.label} · ${PRESET_CATEGORY_LABELS[preset.category]}`
                  : document.formatPreset === "custom"
                    ? "Eigene Größe"
                    : document.formatPreset)}
            </p>
            <p className="mt-1 text-xs text-zinc-400">
              {formatRelative(document.updatedAt)} · {formatBytes(document.sizeBytes)}
            </p>
          </div>
        </Link>
        {menuItems ? (
          <div className="absolute top-2 right-2 opacity-0 transition group-hover:opacity-100 has-[[data-state=open]]:opacity-100">
            <DropdownMenu>
              <DropdownTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="size-8"
                  aria-label="Dateiaktionen"
                >
                  <MoreHorizontal />
                </Button>
              </DropdownTrigger>
              <DropdownContent>{menuItems}</DropdownContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
    </ContextMenu>
  );
}
